/**
 * MilestoneSearchResults
 *
 * Renders a scrollable list of milestone cards. Each item shows title, related
 * counts (objectives, key results, initiatives), status pill, and end date.
 * Clicking a card navigates to the Company Dashboard focused on the owning initiative.
 *
 * Props
 * - results: Array of milestone items to render.
 *
 * Edge cases
 * - When results is empty, displays a "No milestones found." placeholder.
 */
import React from "react";
import { Box, Divider, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import "./milestone-search-results.scss";
import Status from "../../../../features/request-dashboard/request/status/status";
import { routes } from "../../../../constants/routes";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
interface Milestone {
  id: number;
  title: string;
  keyResults: number[];
  objectives: number[];
  initiative: number;
  endDate: string;
  departmentName: string;
  status: string;
  initiativeCount: number;
  keyresCount: number;
  objectiveCount: number;
}

interface Props {
  results: Milestone[];
}

const MilestoneSearchResults: React.FC<Props> = ({ results }) => {
  const navigate = useNavigate();
  if (results.length === 0) {
    return <div className="no-results">No milestones found.</div>;
  }
  return (
    <OverlayScrollbarsComponent
      className="objectives-search-results scrollable"
      options={{
        scrollbars: {
          theme: "os-theme-dark",
          autoHide: "scroll",
          autoHideDelay: 400,
        },
      }}
    >
      {results.map((milestone) => {
        return (
          <Box
            key={milestone.id}
            className="objective-card"
            sx={{ cursor: "pointer" }}
            onClick={() => {
              const queryParams = new URLSearchParams();
              queryParams.set("dashboardTab", "Initiatives");
              queryParams.set("initiativeId", String(milestone.initiative));
              navigate(`${routes.COMPANYDASHBOARD}?${queryParams.toString()}`);
            }}
          >
            {/* Left Section */}
            <Box className="objective-left">
              <Typography variant="subtitle1">{milestone.title}</Typography>

              <Box className="objective-meta">
                <span>
                  Objectives <strong>{milestone.objectiveCount}</strong>
                </span>
                <Divider
                  orientation="vertical"
                  flexItem
                  style={{ marginRight: "8px", marginTop: "8px" }}
                />
                <span>
                  Key Results <strong>{milestone.keyresCount}</strong>
                </span>
                <Divider
                  orientation="vertical"
                  flexItem
                  style={{ marginRight: "8px", marginTop: "8px" }}
                />
                <span>
                  Initiatives <strong>{milestone.initiativeCount}</strong>
                </span>
              </Box>
              <Typography
                className="progress-wrapper"
                variant="caption"
                style={{ marginTop: "8px" }}
              >
                <Status status={milestone.status} className="status" />
              </Typography>
            </Box>

            <Box>
              <span className="milestone-date">
                Date: <strong>{milestone.endDate}</strong>
              </span>
            </Box>
          </Box>
        );
      })}
    </OverlayScrollbarsComponent>
  );
};

export default MilestoneSearchResults;
